/**
 * Подписи и разбор уведомлений.
 *
 * `payload` бэк отдаёт как `json.RawMessage` — спека его не описывает, поэтому
 * всё чтение здесь защитное: любой ключ может отсутствовать, и тогда строка
 * собирается из одной подписи типа. Как только бэк опишет payload, останется
 * дополнить `PAYLOAD_TITLE_KEYS` и ссылки.
 */
import type { NotificationTypeV2 } from '../api/notifications';

/** Все типы событий — по ним строится матрица настроек. */
export const NOTIFICATION_TYPES: NotificationTypeV2[] = [
  'task_assigned',
  'task_status_changed',
  'task_deadline_soon',
  'task_commented',
  'reminder',
  'deal_stage_changed',
  'deal_assigned',
  'calendar_event_created',
  'calendar_event_updated',
  'break_ended',
];

export const NOTIFICATION_TYPE_LABELS: Record<NotificationTypeV2, string> = {
  task_assigned: 'Назначена задача',
  task_status_changed: 'Изменился статус задачи',
  task_deadline_soon: 'Скоро дедлайн задачи',
  task_commented: 'Комментарий в задаче',
  reminder: 'Напоминание по задаче',
  deal_stage_changed: 'Сделка сменила стадию',
  deal_assigned: 'Назначена сделка',
  calendar_event_created: 'Новое событие в календаре',
  calendar_event_updated: 'Событие календаря изменено',
  break_ended: 'Перерыв закончился',
};

/**
 * Каналы доставки. Список фронтовый: бэк не валидирует `channel` вовсе и
 * дефолтов не отдаёт, так что перечень задаём сами — см. шапку api-модуля.
 */
export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'push'] as const;

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> =
  {
    in_app: 'В приложении',
    email: 'Почта',
    push: 'Push',
  };

/** Ключи, под которыми в payload может лежать заголовок сущности. */
const PAYLOAD_TITLE_KEYS = [
  'title',
  'taskTitle',
  'text',
  'message',
  'subject',
  'name',
  'dealTitle',
  'eventTitle',
];

type PayloadRecord = Record<string, unknown>;

/** Разбирает payload в объект, что бы бэк ни прислал (объект, строка, null). */
export function readPayload(payload: unknown): PayloadRecord {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as PayloadRecord;
  }
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload);

      return parsed && typeof parsed === 'object'
        ? (parsed as PayloadRecord)
        : {};
    } catch {
      return {};
    }
  }

  return {};
}

function readString(source: PayloadRecord, keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

/** Заголовок уведомления: подпись типа, а под ней — что известно из payload. */
export function notificationTitle(type: unknown): string {
  const key = String(type ?? '') as NotificationTypeV2;

  return NOTIFICATION_TYPE_LABELS[key] ?? 'Уведомление';
}

/** Вторая строка карточки: заголовок сущности, если бэк его прислал. */
export function notificationText(payload: unknown): string {
  return readString(readPayload(payload), PAYLOAD_TITLE_KEYS);
}

/**
 * Куда вести по клику. Ссылку строим только на задачу: остальные сущности
 * нового бэка своих экранов пока не имеют.
 */
export function notificationLink(payload: unknown): string | null {
  const taskId = readString(readPayload(payload), ['taskId', 'task_id']);

  return taskId ? `/tasks-v2/${taskId}` : null;
}
